import React, { useMemo, useState } from "react";
import "./App.css";

import SidebarNav from "./components/SidebarNav";
import TopBar from "./components/TopBar";
import Dashboard from "./components/Dashboard";
import AlertsPanel from "./components/AlertsPanel";
import PatientList from "./components/PatientList";
import PatientProfile from "./components/PatientProfile";
import NudgePlanBuilder from "./components/NudgePlanBuilder";
import BehaviorHistory from "./components/BehaviorHistory";
import PilotOverview from "./components/PilotOverview";
import PilotParticipants from "./components/PilotParticipants";
import PilotPreflight from "./components/PilotPreflight";
import PilotShadowState from "./components/PilotShadowState";
import PilotAlerts from "./components/PilotAlerts";
import PilotSystemStatus from "./components/PilotSystemStatus";
import PilotAuditTrail from "./components/PilotAuditTrail";
import PilotOperationsQueue from "./components/PilotOperationsQueue";
import PilotInvites from "./components/PilotInvites";
import RiskSafetyPanel from "./components/RiskSafetyPanel";
import DevLogsViewer from "./components/DevLogsViewer";

const DEFAULT_BASE_URL = "https://staging-api.keyrd.org";

const VIEW_TITLES = {
  dashboard: "Dashboard",
  alerts: "Alerts",
  patients: "Patient List",
  profile: "Patient Profile",
  nudgePlan: "Nudge Plan Builder",
  behaviorHistory: "Behavior History",
  pilotOverview: "Pilot Overview",
  pilotParticipants: "Participants",
  pilotPreflight: "Preflight",
  pilotShadow: "Shadow State",
  pilotAlerts: "Pilot Alerts",
  governance: "Governance",
  audit: "Audit Trail",
  pilotQueue: "Operations Queue",
  pilotInvites: "Invites / Cohorts",
  riskSafety: "Risk & Safety",
  developerLogs: "Developer Logs",
};

const VIEW_DESCRIPTIONS = {
  dashboard: "Global pilot snapshot for patients, activity, safety, and operational readiness.",
  alerts: "Prioritized drift, registry, and safety alerts for RDN review.",
  patients: "RDN-scoped patient panel. Select a patient to inspect profile, nudge planning, and behavior history.",
  profile: "Patient-level context, care goals, safety notes, and current state.",
  nudgePlan: "Prepare structured nudge plans while preserving RDN primacy and system guardrails.",
  behaviorHistory: "Behavioral and feedback history for the selected patient.",
  pilotOverview: "Pilot health, activity, safety, and operations snapshot.",
  pilotParticipants: "Participant status and enrollment visibility.",
  pilotPreflight: "Cohort-level readiness checks before deeper operational validation.",
  pilotShadow: "Shadow-state view for evaluating behavior inference and policy traces.",
  pilotAlerts: "Pilot-level operational exceptions and escalation signals.",
  governance: "Release, registry, protocol, safety, and runtime truth page.",
  audit: "Pilot audit trail for console-visible operational events.",
  pilotQueue: "Operational queue for bounded pilot workflows.",
  pilotInvites: "Invite and cohort management. Invite execution remains separately authorized.",
  riskSafety: "Safety, freeze, and escalation state across the pilot surface.",
  developerLogs: "Developer diagnostics for bounded debugging.",
};

function App() {
  const [activeView, setActiveView] = useState("dashboard");
  const [baseUrl, setBaseUrlState] = useState(
    window.localStorage.getItem("keyrd_console_base_url") || DEFAULT_BASE_URL
  );
  const [rdnToken, setRdnTokenState] = useState(
    window.localStorage.getItem("keyrd_console_rdn_token") || ""
  );
  const [selectedPatientId, setSelectedPatientId] = useState("");
  const [lastCheck, setLastCheck] = useState(null);

  const auth = useMemo(
    () => ({
      baseUrl,
      token: rdnToken,
      rdnToken,
      bearerToken: rdnToken,
      headers: rdnToken ? { Authorization: `Bearer ${rdnToken}` } : {},
    }),
    [baseUrl, rdnToken]
  );

  const selectedPatient = useMemo(
    () => ({
      patient_id: selectedPatientId,
      patientId: selectedPatientId,
      id: selectedPatientId,
    }),
    [selectedPatientId]
  );

  function setBaseUrl(next) {
    setBaseUrlState(next);
    window.localStorage.setItem("keyrd_console_base_url", next);
  }

  function setRdnToken(next) {
    setRdnTokenState(next);
    window.localStorage.setItem("keyrd_console_rdn_token", next);
  }

  function handleCheckRdn() {
    setLastCheck(new Date().toISOString());
  }

  function handleSelectedPatient(patient) {
    const nextId =
      patient?.patient_id ||
      patient?.patientId ||
      patient?.id ||
      patient ||
      "";
    setSelectedPatientId(String(nextId || ""));
  }

  function commonProps(extra = {}) {
    return {
      auth,
      baseUrl,
      setBaseUrl,
      rdnToken,
      setRdnToken,
      token: rdnToken,
      selectedPatientId,
      setSelectedPatientId,
      selectedPatient,
      setSelectedPatient: handleSelectedPatient,
      onSelectPatient: handleSelectedPatient,
      activeView,
      setActiveView,
      onNavigate: setActiveView,
      ...extra,
    };
  }

  function renderActiveView() {
    switch (activeView) {
      case "alerts":
        return <AlertsPanel {...commonProps()} />;
      case "patients":
        return <PatientList {...commonProps()} />;
      case "profile":
        return <PatientProfile {...commonProps()} />;
      case "nudgePlan":
        return <NudgePlanBuilder {...commonProps()} />;
      case "behaviorHistory":
        return <BehaviorHistory {...commonProps()} />;
      case "pilotOverview":
        return <PilotOverview {...commonProps()} />;
      case "pilotParticipants":
        return <PilotParticipants {...commonProps()} />;
      case "pilotPreflight":
        return <PilotPreflight {...commonProps()} />;
      case "pilotShadow":
        return <PilotShadowState {...commonProps()} />;
      case "pilotAlerts":
        return <PilotAlerts {...commonProps()} />;
      case "governance":
        return <PilotSystemStatus {...commonProps()} />;
      case "audit":
        return <PilotAuditTrail {...commonProps()} />;
      case "pilotQueue":
        return <PilotOperationsQueue {...commonProps()} />;
      case "pilotInvites":
        return <PilotInvites {...commonProps()} />;
      case "riskSafety":
        return <RiskSafetyPanel {...commonProps()} />;
      case "developerLogs":
        return <DevLogsViewer {...commonProps()} />;
      case "dashboard":
      default:
        return <Dashboard {...commonProps({ lastCheck })}  rdnToken={rdnToken} authToken={rdnToken} rdnContext={null} currentRdn={null}/>;
    }
  }

  const title = VIEW_TITLES[activeView] || "Dashboard";
  const description = VIEW_DESCRIPTIONS[activeView] || VIEW_DESCRIPTIONS.dashboard;
  const ready = Boolean(baseUrl && rdnToken);

  return (
    <div className="krd-shell">
      <SidebarNav
        activeView={activeView}
        setActiveView={setActiveView}
        onNavigate={setActiveView}
      
          activeTab={activeView}
        
          onTabChange={setActiveView}
        />

      <div className="krd-main">
        <TopBar
          baseUrl={baseUrl}
          setBaseUrl={setBaseUrl}
          onBaseUrlChange={setBaseUrl}
          rdnToken={rdnToken}
          setRdnToken={setRdnToken}
          onTokenChange={setRdnToken}
          auth={auth}
          onCheckRdn={handleCheckRdn}
          lastCheck={lastCheck}
          connectionState={ready ? "ready" : "idle"}
        
          token={rdnToken}
        />

        <main className="krd-workspace">
          <section className="krd-page-heading">
            <div>
              <p className="krd-kicker">KeyRD RDN Console</p>
              <h1>{title}</h1>
              <p>{description}</p>
            </div>

            <div className={`krd-status-card ${ready ? "ready" : "idle"}`}>
              <span>{ready ? "Ready" : "Idle"}</span>
              <strong>{ready ? "RDN context available" : "Paste bearer token"}</strong>
            </div>
          </section>

          <section className="krd-view-frame">{renderActiveView()}</section>
        </main>
      </div>
    </div>
  );
}

export default App;
